import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, HeartHandshake, Sparkles, Mail } from 'lucide-react';
import AppLogo from '../components/AppLogo';
import CareProfileResults from '../components/assessments/CareProfileResults';
import { ScaleQuestion, SingleQuestion, TextQuestion } from '../components/assessments/QuestionInputs';

const AID = 'care_profile';
const LS_KEY = 'public_care_profile_responses';
const LS_SESSION_KEY = 'public_care_profile_session_id';
const LS_STARTED_KEY = 'public_care_profile_started';

export default function PublicCareProfilePage() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [responses, setResponses] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LS_KEY)) || {}; } catch { return {}; }
  });
  const [stage, setStage] = useState('intro'); // intro | section | email | results
  const [sectionIdx, setSectionIdx] = useState(0);
  const [results, setResults] = useState(null);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/assessments/${AID}/config`)
      .then(res => setConfig(res.data))
      .catch(() => toast.error('Could not load the assessment'));
  }, []);

  useEffect(() => { localStorage.setItem(LS_KEY, JSON.stringify(responses)); }, [responses]);

  // Fire a one-time start event when the user actually begins the quiz.
  const recordStart = () => {
    if (localStorage.getItem(LS_STARTED_KEY)) return;
    let sid = localStorage.getItem(LS_SESSION_KEY);
    if (!sid) {
      sid = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
      localStorage.setItem(LS_SESSION_KEY, sid);
    }
    axios.post(`${API}/public/assessments/${AID}/start`, { session_id: sid }).catch(() => {});
    localStorage.setItem(LS_STARTED_KEY, '1');
  };

  const setAnswer = (qid, value) => setResponses(prev => ({ ...prev, [qid]: value }));

  const visibleQuestions = (section) => section.questions.filter(q =>
    !q.show_if || responses[q.show_if.question] === q.show_if.equals
  );

  const sectionComplete = (section) => visibleQuestions(section)
    .filter(q => !q.optional)
    .every(q => responses[q.id] !== undefined && responses[q.id] !== '');

  const handleNext = () => {
    const section = config.sections[sectionIdx];
    if (!sectionComplete(section)) { toast.error('Please answer every question in this section'); return; }
    if (sectionIdx < config.sections.length - 1) {
      setSectionIdx(sectionIdx + 1);
      window.scrollTo({ top: 0 });
    } else {
      setStage('email');
      window.scrollTo({ top: 0 });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/public/assessments/${AID}/submit`, { email, name, responses });
      setResults({ scores: res.data.scores });
      setStage('results');
      localStorage.removeItem(LS_KEY);
      toast.success('Your Care Profile is ready — we also emailed it to you!');
    } catch (err) { toast.error(err.response?.data?.detail || 'Something went wrong'); }
    setSubmitting(false);
  };

  if (!config) {
    return <div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-600" /></div>;
  }

  if (stage === 'results' && results) {
    return <CareProfileResults results={results} publicMode onRetake={() => { setResponses({}); setResults(null); setSectionIdx(0); setStage('intro'); localStorage.removeItem(LS_STARTED_KEY); }} />;
  }

  if (stage === 'intro') {
    return (
      <div className="min-h-screen pb-24 bg-background" data-testid="public-care-profile-intro">
        <div className="max-w-lg mx-auto px-6 pt-12 space-y-6 text-center">
          <div className="flex justify-center"><AppLogo /></div>
          <HeartHandshake className="w-12 h-12 text-cyan-600 mx-auto" />
          <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>{config.intro.heading}</h1>
          <p className="text-sm text-muted-foreground leading-relaxed text-left">{config.intro.body}</p>
          <p className="text-xs text-muted-foreground">{config.estimated_minutes} minutes · no account needed</p>
          <Button className="btn-primary w-full" onClick={() => { recordStart(); setStage('section'); }} data-testid="public-start-btn">
            Start the Quiz <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <button className="text-xs text-muted-foreground underline" onClick={() => navigate('/individuals')}>Back to home</button>
        </div>
      </div>
    );
  }

  if (stage === 'email') {
    return (
      <div className="min-h-screen pb-24 bg-background" data-testid="public-email-gate">
        <div className="max-w-md mx-auto px-6 pt-16 space-y-5 text-center">
          <Mail className="w-10 h-10 text-cyan-600 mx-auto" />
          <h2 className="text-xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>Your Care Profile™ is ready!</h2>
          <p className="text-sm text-muted-foreground">Tell us where to send it — you'll see your results right away and get a copy by email.</p>
          <form onSubmit={handleSubmit} className="space-y-3 text-left">
            <Input placeholder="Your first name (optional)" value={name} onChange={(e) => setName(e.target.value)} data-testid="lead-name-input" className="rounded-xl" />
            <Input type="email" required placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="lead-email-input" className="rounded-xl" />
            <Button type="submit" className="btn-primary w-full" disabled={submitting} data-testid="lead-submit-btn">
              {submitting ? 'Calculating...' : <span className="flex items-center justify-center">See My Care Profile <Sparkles className="w-4 h-4 ml-2" /></span>}
            </Button>
          </form>
          <p className="text-[11px] text-muted-foreground">We'll email your results and how CircleUpSquad can help. No spam, ever.</p>
        </div>
      </div>
    );
  }

  const section = config.sections[sectionIdx];
  const pct = Math.round((sectionIdx / config.sections.length) * 100);

  return (
    <div className="min-h-screen pb-32 bg-background" data-testid="public-care-profile-section">
      <div className="surface-header sticky top-0 z-10">
        <div className="max-w-lg mx-auto px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs text-muted-foreground">Section {sectionIdx + 1} of {config.sections.length}</p>
            <p className="text-xs font-semibold text-cyan-600">{pct}%</p>
          </div>
          <Progress value={pct} className="h-1.5" />
        </div>
      </div>
      <div className="max-w-lg mx-auto px-4 pt-6 space-y-4">
        <div>
          <h2 className="text-lg font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="section-title">{section.title}</h2>
          <p className="text-sm text-muted-foreground">{section.description}</p>
        </div>
        {visibleQuestions(section).map(q => {
          if (q.type === 'scale') return <ScaleQuestion key={q.id} q={q} value={responses[q.id]} onChange={setAnswer} />;
          if (q.type === 'single') return <SingleQuestion key={q.id} q={q} value={responses[q.id]} onChange={setAnswer} />;
          return <TextQuestion key={q.id} q={q} value={responses[q.id]} onChange={setAnswer} />;
        })}
        <div className="flex gap-3 pt-2">
          {sectionIdx > 0 && (
            <Button variant="outline" onClick={() => { setSectionIdx(sectionIdx - 1); window.scrollTo({ top: 0 }); }} data-testid="prev-section-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
          )}
          <Button className="btn-primary flex-1" onClick={handleNext} data-testid="next-section-btn">
            {sectionIdx === config.sections.length - 1
              ? <span className="flex items-center">Get My Results <Sparkles className="w-4 h-4 ml-2" /></span>
              : <span className="flex items-center">Next <ArrowRight className="w-4 h-4 ml-2" /></span>}
          </Button>
        </div>
      </div>
    </div>
  );
}
